/**
 * The login card (web-plan.md F1): the desktop's `.login` structure — one
 * card, a segmented Sign in / Sign up, the field stack, the age-and-terms
 * check (Store policy), the submit — wearing the shared sheet's classes.
 */
import { type JSX, Show, createSignal } from "solid-js";

import { ApiError } from "../lib/api/rest";
import type { Dict } from "../lib/i18n";
import type { Runtime } from "../store/runtime";
import { Button, Checkbox, Icon, SegmentedControl } from "../components/ds";

export function Login(props: { rt: Runtime; t: Dict }): JSX.Element {
  const t = props.t;
  const [mode, setMode] = createSignal<0 | 1>(0); // 0 = sign in, 1 = sign up
  const [username, setUsername] = createSignal("");
  const [email, setEmail] = createSignal("");
  const [password, setPassword] = createSignal("");
  const [reveal, setReveal] = createSignal(false);
  const [agreed, setAgreed] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  const submit = async () => {
    setError(null);
    try {
      if (mode() === 0) {
        await props.rt.login(username(), password());
      } else {
        if (!agreed()) return;
        await props.rt.register(username(), email(), password());
      }
    } catch (e) {
      setError(describe(e, t));
    }
  };

  return (
    <div class="login">
      <div class="login__card echo-material-thick">
        <div class="login__title">{t.loginTitle()}</div>
        <SegmentedControl
          items={[t.loginSubmit(), t.registerSubmit()]}
          selected={mode()}
          full
          onSelect={(i) => {
            setMode(i as 0 | 1);
            setError(null);
          }}
        />
        <div style="display:flex;flex-direction:column">
          <div class="login__fields">
            <div class="login__field">
              <input
                type="text"
                placeholder={t.loginUsername()}
                value={username()}
                autocomplete="username"
                onInput={(e) => setUsername(e.currentTarget.value)}
                onKeyDown={(e) => e.key === "Enter" && void submit()}
              />
            </div>
            <Show when={mode() === 1}>
              <div class="login__field">
                <input
                  type="text"
                  placeholder={t.loginEmail()}
                  value={email()}
                  autocomplete="email"
                  inputmode="email"
                  onInput={(e) => setEmail(e.currentTarget.value)}
                />
              </div>
            </Show>
            <div class="login__field">
              <input
                type={reveal() ? "text" : "password"}
                placeholder={t.loginPassword()}
                value={password()}
                autocomplete={mode() === 0 ? "current-password" : "new-password"}
                onInput={(e) => setPassword(e.currentTarget.value)}
                onKeyDown={(e) => e.key === "Enter" && void submit()}
              />
              <button type="button" class="login__reveal" onClick={() => setReveal(!reveal())}>
                <Icon name={reveal() ? "visibility_off" : "visibility"} size={17} />
              </button>
            </div>
          </div>
          <Show when={error()}>
            <div class="login__error">{error()}</div>
          </Show>
        </div>
        <Show when={mode() === 1}>
          <Checkbox on={agreed()} onToggle={() => setAgreed(!agreed())}>
            {t.loginAgeGate()}{" "}
            <a class="login__legal" href="/terms" target="_blank" rel="noreferrer">
              terms
            </a>{" "}
            ·{" "}
            <a class="login__legal" href="/privacy" target="_blank" rel="noreferrer">
              privacy
            </a>
          </Checkbox>
        </Show>
        <Button
          size="large"
          kind="prominent"
          icon="check"
          full
          disabled={
            props.rt.state.busy ||
            username().trim() === "" ||
            password() === "" ||
            (mode() === 1 && (!agreed() || email().trim() === ""))
          }
          onClick={() => void submit()}
        >
          {mode() === 0 ? t.loginSubmit() : t.registerSubmit()}
        </Button>
      </div>
    </div>
  );
}

function describe(e: unknown, t: Dict): string {
  if (e instanceof ApiError) {
    if (e.signup === "name_taken") return t.loginFailed("username is taken");
    if (e.signup === "email_taken") return t.loginFailed("email is taken");
    if (e.signup === "too_many_attempts") return t.loginFailed("too many attempts — wait a bit");
    return t.loginFailed(e.message);
  }
  return t.loginFailed(e instanceof Error ? e.message : String(e));
}
